// UploadStep.jsx
import React, { useState, useRef, useEffect } from 'react';
import Tesseract from 'tesseract.js';
import useOcrExtractor from '../hooks/useOcrExtractor';
import { HiArrowRight, HiArrowLeft } from 'react-icons/hi2';
import { HiOutlineRefresh } from 'react-icons/hi';
import { FiTrash2 } from 'react-icons/fi';
import { getDeviceDetails, getDeviceFields } from '../utils/api';

const buildMeta = ({ model, fccId, serialNumber, sku }) => {
  const meta = [];
  if (model) meta.push({ label: 'Model', value: model });
  if (fccId) meta.push({ label: 'FCC ID', value: fccId });
  if (serialNumber) meta.push({ label: 'Serial Number', value: serialNumber });
  if (sku) meta.push({ label: 'SKU', value: sku });
  return meta;
};

export default function UploadStep({ onNext, onBack, setOcrData, ocrData }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(ocrData?.imageUrl || null);
  const [eui, setEui] = useState(ocrData?.eui || '');
  const [metadata, setMetadata] = useState(ocrData?.metadata || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [manualMode, setManualMode] = useState(!!ocrData?.eui);

  const [cameraActive, setCameraActive] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const extractInfoFromText = useOcrExtractor();

  // ---- Helper: wait for <video> to be ready ----
  const waitForVideoReady = (videoEl) =>
    new Promise((resolve, reject) => {
      if (!videoEl) return reject(new Error('No video element'));
      if (videoEl.readyState >= 2 && videoEl.videoWidth) return resolve();
      const onReady = () => {
        videoEl.removeEventListener('canplay', onReady);
        videoEl.removeEventListener('loadeddata', onReady);
        resolve();
      };
      videoEl.addEventListener('canplay', onReady, { once: true });
      videoEl.addEventListener('loadeddata', onReady, { once: true });
    });

  // Clean up camera and object URLs on unmount
  useEffect(() => {
    return () => {
      stopCamera();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCamera = async () => {
    try {
      setError('');
      setCameraReady(false);
      setCameraActive(true); // show camera UI immediately

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try {
          await videoRef.current.play();
        } catch (err) {
          console.debug('video.play() ignored:', err);
        }
        await waitForVideoReady(videoRef.current);
        setCameraReady(true);
      }
    } catch (err) {
      console.error(err);
      setError('Unable to access camera. Please allow permission or use upload instead.');
      setCameraActive(false);
      setCameraReady(false);
    }
  };

  const stopCamera = () => {
    try {
      const stream = videoRef.current?.srcObject;
      if (stream) stream.getTracks().forEach((t) => t.stop());
      if (videoRef.current) videoRef.current.srcObject = null;
    } catch (e) {
      console.debug('stopCamera ignored:', e);
    }
    setCameraActive(false);
    setCameraReady(false);
  };

  // ---- Async function, no async Promise executor ----
  const captureFrameToBlob = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) {
      throw new Error('Camera not available');
    }

    if (video.readyState < 2 || !video.videoWidth) {
      await waitForVideoReady(video);
    }

    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);

    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => {
          if (!b) return reject(new Error('Could not create image blob'));
          resolve(b);
        },
        'image/jpeg',
        0.92
      );
    });

    return blob;
  };

  // Optional: manual capture only (no OCR)
  const captureImage = async () => {
    try {
      setError('');
      const blob = await captureFrameToBlob();
      const file = new File([blob], 'capture.jpg', { type: 'image/jpeg' });

      if (previewUrl) URL.revokeObjectURL(previewUrl);
      const url = URL.createObjectURL(file);

      setSelectedFile(file);
      setPreviewUrl(url);

      // reset OCR-related fields
      setEui('');
      setMetadata([]);
      setManualMode(false);
      setOcrData({ eui: '', imageUrl: url, metadata: [] });
      stopCamera();
    } catch (e) {
      console.error(e);
      setError(e.message || 'Camera not ready yet. Try again.');
    }
  };

  // Capture & OCR (user clicks this after camera is open)
  const captureAndScan = async () => {
    try {
      setError('');
      setLoading(true);

      const blob = await captureFrameToBlob();

      // Update preview immediately
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      const url = URL.createObjectURL(blob);
      const file = new File([blob], 'capture.jpg', { type: 'image/jpeg' });

      setPreviewUrl(url);
      setSelectedFile(file);

      // Run OCR on the Blob/File (not blob URL)
      const {
        data: { text },
      } = await Tesseract.recognize(blob, 'eng', {
        logger: (m) => console.log(m), // progress/debug
      });

      const { devEui, serialNumber, model, fccId, sku } = extractInfoFromText(text);
      const meta = buildMeta({ model, fccId, serialNumber, sku });

      let deviceInfo = null;
      let deviceType = '';
      let deviceFields = [];
      let guidePdf = '';
      let guideImage = '';

      try {
        if (devEui) {
          try {
            deviceInfo = await getDeviceDetails(devEui);
          } catch (err) {
            console.error('Failed to fetch device details:', err);
            setError(err?.response?.data?.message || 'Unable to fetch device details.'); 
          }

          deviceType = deviceInfo?.deviceType; // guard because deviceInfo could be null on 404
          if (deviceType) {
            try {
              deviceFields = await getDeviceFields(deviceType);
              if (deviceFields?.guide) {
                guidePdf = deviceFields.guide.pdf;
                guideImage = deviceFields.guide.image;
              }
            } catch (err) {
              console.error('Failed to fetch device fields:', err);
              setError(err?.response?.data?.message || 'Unable to fetch device feilds.'); 
            }
          }
        }
      } catch (e) {
        console.warn('No device info or fields found. Proceeding with OCR results only.');
        console.error(e);
        setError(e.message || 'Capture/scan failed. Try again.');
      }

      if (!devEui) {
        setError('Could not find a valid DevEUI. You can continue manually.');
        setManualMode(true);
        setMetadata(meta);
        return;
      }

      setEui(devEui);
      setMetadata(meta);
      setOcrData({
        eui: devEui,
        imageUrl: url,
        metadata: meta,
        deviceType,
        deviceInfo,
        deviceFields,
        guidePdf,
        guideImage,
      });
    } catch (e) {
      console.error(e);
      setError(e.message || 'Capture/scan failed. Try again.');
    } finally {
      setLoading(false);
      // Close camera after scan (feel free to remove this if you want to keep preview open)
      stopCamera();
    }
  };

  const handleSelectedFile = (file) => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    const url = URL.createObjectURL(file);
    setSelectedFile(file);
    setPreviewUrl(url);

    setEui('');
    setMetadata([]);
    setManualMode(false);
    setOcrData({ eui: '', imageUrl: url, metadata: [] });
    setError('');
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please upload or capture an image.');
      return;
    }
    setLoading(true);
    try {
      // Use the actual File (not the blob URL)
      const {
        data: { text },
      } = await Tesseract.recognize(selectedFile, 'eng', {
        logger: (m) => console.log(m),
      });

      const { devEui, serialNumber, model, fccId, sku } = extractInfoFromText(text);
      const meta = buildMeta({ model, fccId, serialNumber, sku });

      let deviceInfo = null;
      let deviceType = '';
      let deviceFields = [];
      let guidePdf = '';
      let guideImage = '';

      try {
        if (devEui) {
          try {
            deviceInfo = await getDeviceDetails(devEui);
          } catch (err) {
            console.error('Failed to fetch device details:', err);
            setError(err?.response?.data?.message || 'Unable to fetch device details.'); 
          }

          deviceType = deviceInfo?.deviceType;
          if (deviceType) {
            try {
              deviceFields = await getDeviceFields(deviceType);
              if (deviceFields?.guide) {
                guidePdf = deviceFields.guide.pdf;
                guideImage = deviceFields.guide.image;
              }
            } catch (err) {
              console.error('Failed to fetch device fields:', err);
              setError(err?.response?.data?.message|| 'Unable to fetch device fields.'); 
            }
          }
        }
      } catch (e) {
        console.warn('No device info or fields found. Proceeding with OCR results only.');
        console.error(e);
        setError(e.message || 'Capture/scan failed. Try again.');
      }

      if (!devEui) {
        setError('Could not find a valid DevEUI. You can continue manually.');
        setManualMode(true);
        setMetadata(meta);
        setLoading(false);
        return;
      }

      setEui(devEui);
      setMetadata(meta);
      setOcrData({
        eui: devEui,
        imageUrl: previewUrl,
        metadata: meta,
        deviceType,
        deviceInfo,
        deviceFields,
        guidePdf,
        guideImage,
      });
    } catch (err) {
      console.error(err);
      setError('Image processing failed. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (!eui.trim()) {
      setError('DevEUI is required.');
      return;
    }
    setOcrData((prev) => ({ ...prev, eui, imageUrl: previewUrl, metadata }));
    onNext();
  };

  const updateMetadata = (index, key, value) => {
    const updated = [...metadata];
    updated[index][key] = value;
    setMetadata(updated);
  };

  const removeMetadata = (index) => setMetadata(metadata.filter((_, i) => i !== index));
  const addMetadataRow = () => setMetadata([...metadata, { label: '', value: '' }]);

  return (
    <div className="space-y-10">
      <h1 className="text-2xl font-semibold text-gray-800">Scan Device Label</h1>

      {/* Buttons for camera or upload */}
      <div className="flex flex-wrap gap-4">
        <button
          onClick={startCamera}
          className="bg-primary text-white px-4 py-2 rounded hover:bg-primary/90 text-sm"
        >
          Scan with Camera
        </button>

        <input
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
          id="fileUpload"
        />
        <label
          htmlFor="fileUpload"
          className="bg-primary text-white px-4 py-2 rounded cursor-pointer hover:bg-primary/90 text-sm"
        >
          Upload from Gallery
        </label>
      </div>

      {/* Camera preview */}
      {cameraActive && (
        <div className="mt-4 space-y-3">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full max-h-64 rounded-md border"
            onLoadedMetadata={() => {
              if (videoRef.current) {
                videoRef.current.play().catch((err) =>
                  console.debug('video.play() on metadata ignored:', err)
                );
              }
            }}
            onCanPlay={() => setCameraReady(true)}
          />
          <canvas ref={canvasRef} className="hidden" />

          <div className="flex flex-wrap gap-3">
            <button
              onClick={captureAndScan}
              disabled={!cameraReady || loading}
              className={`px-4 py-2 rounded text-sm text-white ${
                !cameraReady || loading
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-primary hover:bg-primary/90'
              }`}
            >
              {loading ? 'Scanning…' : 'Capture & Scan'}
            </button>

            {/* Optional manual capture (no OCR) */}
            <button
              onClick={captureImage}
              disabled={!cameraReady || loading}
              className="bg-green-600 disabled:bg-gray-300 text-white px-4 py-2 rounded text-sm"
            > Capture </button>

            <button
              onClick={stopCamera}
              className="bg-gray-200 text-gray-800 px-4 py-2 rounded text-sm hover:bg-gray-300"
            > Cancel </button>
          </div>

          {!cameraReady && (
            <p className="text-xs text-gray-500">Initializing camera… hold on a sec.</p>
          )}
        </div>
      )}

      {/* Preview and OCR trigger (for uploaded/manual images) */}
      {previewUrl && (
        <div className="mt-4 space-y-3">
          <img src={previewUrl} alt="Preview" className="max-h-64 rounded-md border" />
          {!eui && (
            <button
              onClick={handleUpload}
              disabled={loading}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md transition ${
                loading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-primary text-white hover:bg-primary/90'
              }`}
            >
              {loading && <HiOutlineRefresh className="animate-spin h-5 w-5" />}
              {loading ? 'Scanning...' : 'Scan Image'}
            </button>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      )}

      {/* Device Guide */}
      {ocrData?.guideImage && (
        <div className="space-y-2">
          <h2 className="text-lg font-semibold text-gray-700">Installation Guide</h2>
          <div className="mt-3 flex items-center gap-4">
            <img
              src={ocrData.guideImage}
              alt="Installation Guide"
              className="w-16 h-16 object-contain border rounded"
            />
            {ocrData.guidePdf && (
              <a
                href={ocrData.guidePdf}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline text-sm"
              >
                Open Installation PDF
              </a>
            )}
          </div>
        </div>
      )}

      {/* DevEUI input */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">DevEUI</label>
          <input
            type="text"
            value={eui}
            onChange={(e) => setEui(e.target.value)}
            placeholder="Enter or verify DevEUI"
            className="w-full border rounded-md px-4 py-2 text-sm"
          />
        </div>
      </div>

      {/* Metadata block */}
      {(manualMode || metadata.length > 0) && (
        <div className="mt-3 bg-gray-50 border rounded-md p-3 text-sm space-y-2">
          <p className="font-medium text-gray-800 mb-1">Metadata:</p>
          {metadata.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <input
                type="text"
                value={item.label}
                onChange={(e) => updateMetadata(idx, 'label', e.target.value)}
                className="w-1/3 px-1 py-1 border rounded-md text-sm"
                placeholder="Label"
              />
              <input
                type="text"
                value={item.value}
                onChange={(e) => updateMetadata(idx, 'value', e.target.value)}
                className="flex-1 px-1 py-1 border rounded-md text-sm"
                placeholder="Value"
              />
              <button
                onClick={() => removeMetadata(idx)}
                className="text-red-500 hover:text-red-700"
                title="Remove"
              >
                <FiTrash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addMetadataRow}
            className="mt-2 text-sm text-primary hover:underline"
          >
            + Add Metadata Field
          </button>
        </div>
      )}

      <div className="pt-6 flex justify-between">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-gray-200 text-sm text-gray-800 hover:bg-gray-300 transition"
        >
          <HiArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={handleNext}
          className="inline-flex items-center gap-2 px-6 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 transition"
        >
          Next
          <HiArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
